import { Badge, Button, Card, PageHeader, Skeleton, useToast } from "@closer/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, ApiRequestError } from "../../lib/apiClient";
import { useAuth } from "../../lib/authContext";
import { tenantKey } from "../../lib/tenantQuery";

type Politika = { twoFactorRequired: boolean };

/**
 * 2FA ZORUNLULUK POLİTİKASI (CLOS-292 `[046]`).
 *
 * ⚠ Ekranı yoktu: politika yalnız API'den açılabiliyordu. Bu iki yönden kusurluydu —
 * kartın "yetkili kullanıcı politika durumunu değiştirebilir" kriteri karşılanmıyordu ve
 * politika API'den açılsaydı 2FA kuramayan üyeler kilitlenirdi (o ekran da yoktu).
 *
 * ⚠ Görüntüleme ile DEĞİŞTİRME ayrı yetkiler: `organization.view` ile durum okunuyor,
 * değiştirme `organization.security` istiyor. Tek yetkiye bağlasaydık, politikayı
 * görebilen herkes onu değiştirebilirdi.
 *
 * ⚠ Kişisel alanda politika AÇILAMAZ; sunucu da veritabanı da bunu reddediyor. Düğmeyi
 * yine de göstermek, kullanıcıyı reddedilecek bir eyleme davet etmek olurdu.
 */
export function SecurityPolicyPage() {
  const { activeTenantId, activeTenant, can } = useAuth();
  const tenantId = activeTenantId!;
  const toast = useToast();
  const queryClient = useQueryClient();

  const sorgu = useQuery({
    queryKey: tenantKey(tenantId, "guvenlik-politikasi"),
    queryFn: async () =>
      (await api<Politika>(`/api/tenants/${tenantId}/security-policy`, { tenantId })).data,
  });

  const degistir = useMutation({
    mutationFn: async (twoFactorRequired: boolean) =>
      (
        await api<Politika>(`/api/tenants/${tenantId}/security-policy`, {
          method: "PUT",
          tenantId,
          body: JSON.stringify({ twoFactorRequired }),
        })
      ).data,
    onSuccess: async (veri) => {
      await queryClient.invalidateQueries({ queryKey: tenantKey(tenantId, "guvenlik-politikasi") });
      toast.success(
        veri.twoFactorRequired
          ? "İki aşamalı doğrulama zorunlu kılındı"
          : "İki aşamalı doğrulama zorunluluğu kaldırıldı",
      );
    },
    onError: (error) => {
      toast.error(error instanceof ApiRequestError ? error.message : "Politika değiştirilemedi");
    },
  });

  const kisisel = activeTenant?.type === "individual";
  const degistirebilir = can("organization.security") && !kisisel;
  const zorunlu = sorgu.data?.twoFactorRequired === true;

  return (
    <div className="flex w-full flex-col gap-5">
      <PageHeader
        title="Güvenlik Politikası"
        description="Organizasyonunuz için iki aşamalı doğrulama zorunluluğunu yönetin."
      />

      {/*
        ⚠ DAR SÜTUN BAŞLIĞIN ALTINDA (5 Eylül denetimi · C1): kapsayıcıyı daraltmak
        `PageHeader`'ı da daraltıyordu ve başlık kendi sekme şeridiyle hizasız kalıyordu.
        Kapsayıcı genişliği kabuğun işi; darlık yalnız onu isteyen içeriğin.
      */}
      <div className="w-full max-w-2xl">
        <Card
          title="İki Aşamalı Doğrulama"
          actions={
            sorgu.isPending || !degistirebilir ? null : (
              <Button
                type="button"
                size="sm"
                variant={zorunlu ? "secondary" : "primary"}
                loading={degistir.isPending}
                onClick={() => degistir.mutate(!zorunlu)}
              >
                {zorunlu ? "Zorunluluğu kaldır" : "Zorunlu kıl"}
              </Button>
            )
          }
        >
          {sorgu.isPending ? (
            <div role="status" aria-label="Politika yükleniyor">
              <Skeleton lines={2} />
            </div>
          ) : (
            <div className="flex flex-col gap-3">
              <p className="flex items-center gap-2 text-sm text-fg">
                <Badge tone={zorunlu ? "success" : "neutral"}>
                  {zorunlu ? "Zorunlu" : "İsteğe bağlı"}
                </Badge>
              </p>
              {/*
              ⚠ Sonucu ÖNCEDEN söylüyoruz: zorunluluk açıldığında 2FA'sı olmayan üye
              çalışma alanına giremez. Kullanıcı bunu ancak biri şikâyet edince öğrenseydi,
              politika bir güvenlik aracı değil bir sürpriz olurdu.
            */}
              <p className="text-sm text-fg-soft">
                {zorunlu
                  ? "İki aşamalı doğrulaması olmayan üyeler bu çalışma alanına giremez; hesap ayarlarından kurabilirler."
                  : "Zorunlu kıldığınızda, iki aşamalı doğrulaması olmayan üyeler kurana kadar bu çalışma alanına giremez."}
              </p>
              {kisisel ? (
                <p className="text-sm text-warning">
                  Kişisel çalışma alanında bu politika açılamaz.
                </p>
              ) : null}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
